import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { createDoctorsSearchPayload, createLocationsSearchPayload, createOfferingsSearchPayload, createPartnersSearchPayload } from '@lib/core/helpers/partner.helper';
import { PartnersService } from '@lib/core/services/partners.service';

import * as PartnerActions from './partner.actions';
import {
  selectCurrentPartner,
  selectDoctorsContinuationToken,
  selectDoctorsFilter,
  selectDoctorsPageSize,
  selectLocationsContinuationToken,
  selectLocationsFilter,
  selectLocationsPageSize,
  selectOfferingsContinuationToken,
  selectOfferingsFilter,
  selectOfferingsPageSize,
  selectPartnersContinuationToken,
  selectPartnersFilter,
  selectPartnersPageSize,
} from './partner.selectors';

export const setPartnersFilterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(PartnerActions.setPartnersFilter),
      map(() => PartnerActions.fetchPartnersFirstPage())
    );
  },
  { functional: true }
);

export const fetchPartnersFirstPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchPartnersFirstPage),
      tap(() => store.dispatch(PartnerActions.clearPartnersList())),
      withLatestFrom(store.select(selectPartnersFilter), store.select(selectPartnersPageSize)),
      map(([, filter, pageSize]) => {
        const payload = createPartnersSearchPayload({ pageSize, filter, continuationToken: '' });
        return PartnerActions.getPartnersList({ payload });
      })
    );
  },
  { functional: true }
);

export const fetchPartnersNextPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchPartnersNextPage),
      withLatestFrom(store.select(selectPartnersFilter), store.select(selectPartnersPageSize), store.select(selectPartnersContinuationToken)),
      map(([, filter, pageSize, continuationToken]) => {
        const payload = createPartnersSearchPayload({ pageSize, filter, continuationToken });
        return PartnerActions.getPartnersList({ payload });
      })
    );
  },
  { functional: true }
);

export const getPartnersListEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.getPartnersList),
      switchMap(({ payload }) =>
        partnersService.searchPartnersV2(payload).pipe(
          map(response => PartnerActions.getPartnersListSuccess({ response })),
          catchError(error => of(PartnerActions.getPartnersListFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const getPartnerByIdEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.getPartnerById),
      switchMap(({ id }) =>
        partnersService.getPartnerByIdV2(id).pipe(
          map(partner => PartnerActions.getPartnerByIdSuccess({ partner })),
          catchError(error => of(PartnerActions.getPartnerByIdFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const createPartnerEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.createPartner),
      switchMap(({ payload, selectedLogo }) =>
        partnersService.createPartnerV2(payload).pipe(
          switchMap(partner => {
            if (selectedLogo) {
              const formData = new FormData();
              formData.append('file', selectedLogo);
              return partnersService.updatePartnerLogoV2(partner.id, formData).pipe(
                map(updatedPartner => PartnerActions.createPartnerSuccess({ partner: updatedPartner })),
                catchError(() => of(PartnerActions.createPartnerSuccess({ partner })))
              );
            }
            return of(PartnerActions.createPartnerSuccess({ partner }));
          }),
          catchError(error => of(PartnerActions.createPartnerFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const createPartnerSuccessNavigationEffect = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(PartnerActions.createPartnerSuccess),
      tap(({ partner }) => {
        router.navigate(['/partners', partner.id]);
      })
    );
  },
  { functional: true, dispatch: false }
);

export const editPartnerEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.editPartner),
      switchMap(({ payload }) =>
        partnersService.updatePartnerV2(payload).pipe(
          map(partner => PartnerActions.editPartnerSuccess({ partner })),
          catchError(error => of(PartnerActions.editPartnerFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const activatePartnerEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.activatePartner),
      switchMap(({ id }) =>
        partnersService.getPartnerByIdV2(id).pipe(
          switchMap(partner =>
            partnersService.updatePartnerV2({ ...partner, active: true }).pipe(
              map(updatedPartner => PartnerActions.activatePartnerSuccess({ partner: updatedPartner })),
              catchError(error => of(PartnerActions.activatePartnerFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.activatePartnerFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const deactivatePartnerEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.deactivatePartner),
      switchMap(({ id }) =>
        partnersService.getPartnerByIdV2(id).pipe(
          switchMap(partner =>
            partnersService.updatePartnerV2({ ...partner, active: false }).pipe(
              map(updatedPartner => PartnerActions.deactivatePartnerSuccess({ partner: updatedPartner })),
              catchError(error => of(PartnerActions.deactivatePartnerFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.deactivatePartnerFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const setPartnerAvailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.setPartnerAvailable),
      switchMap(({ id }) =>
        partnersService.getPartnerByIdV2(id).pipe(
          switchMap(partner =>
            partnersService.updatePartnerV2({ ...partner, available: true }).pipe(
              map(updatedPartner => PartnerActions.setPartnerAvailableSuccess({ partner: updatedPartner })),
              catchError(error => of(PartnerActions.setPartnerAvailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setPartnerAvailableFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const setPartnerUnavailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.setPartnerUnavailable),
      switchMap(({ id }) =>
        partnersService.getPartnerByIdV2(id).pipe(
          switchMap(partner =>
            partnersService.updatePartnerV2({ ...partner, available: false }).pipe(
              map(updatedPartner => PartnerActions.setPartnerUnavailableSuccess({ partner: updatedPartner })),
              catchError(error => of(PartnerActions.setPartnerUnavailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setPartnerUnavailableFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const deletePartnerEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.deletePartner),
      switchMap(({ id }) =>
        partnersService.deletePartnerV2(id).pipe(
          map(() => PartnerActions.deletePartnerSuccess({ id })),
          catchError(error => of(PartnerActions.deletePartnerFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const uploadPartnerLogoEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.uploadPartnerLogo),
      switchMap(({ partnerId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return partnersService.updatePartnerLogoV2(partnerId, formData).pipe(
          map(partner => PartnerActions.uploadPartnerLogoSuccess({ partner })),
          catchError(error => of(PartnerActions.uploadPartnerLogoFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

// ===== LOCATION EFFECTS =====

export const setLocationsFilterEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setLocationsFilter),
      withLatestFrom(store.select(selectCurrentPartner)),
      map(([, currentPartner]) => {
        if (!currentPartner?.id) {
          return PartnerActions.getLocationsListFailure({ error: 'No current partner selected' });
        }
        return PartnerActions.fetchLocationsFirstPage({ partnerId: currentPartner.id });
      })
    );
  },
  { functional: true }
);

export const fetchLocationsFirstPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchLocationsFirstPage),
      tap(() => store.dispatch(PartnerActions.clearLocationsList())),
      withLatestFrom(store.select(selectLocationsFilter), store.select(selectLocationsPageSize)),
      map(([{ partnerId }, filter, pageSize]) => {
        const payload = createLocationsSearchPayload({ partnerId, pageSize, filter, continuationToken: '' });
        return PartnerActions.getLocationsList({ payload });
      })
    );
  },
  { functional: true }
);

export const fetchLocationsNextPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchLocationsNextPage),
      withLatestFrom(store.select(selectLocationsFilter), store.select(selectLocationsPageSize), store.select(selectLocationsContinuationToken)),
      map(([{ partnerId }, filter, pageSize, continuationToken]) => {
        const payload = createLocationsSearchPayload({ partnerId, pageSize, filter, continuationToken });
        return PartnerActions.getLocationsList({ payload });
      })
    );
  },
  { functional: true }
);

export const getLocationsListEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.getLocationsList),
      switchMap(({ payload }) => {
        return partnersService.searchPartnerLocationsV2(payload.partnerId, payload).pipe(
          map(response => PartnerActions.getLocationsListSuccess({ response })),
          catchError(error => of(PartnerActions.getLocationsListFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const getLocationByIdEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.getLocationById),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.getLocationByIdFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerLocationByIdV2(currentPartner.id, id).pipe(
          map(location => PartnerActions.getLocationByIdSuccess({ location })),
          catchError(error => of(PartnerActions.getLocationByIdFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const createLocationEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.createLocation),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload, selectedLogo }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.createLocationFailure({ error: 'No current partner selected' }));
        }
        return partnersService.createPartnerLocationV2(payload.partnerId, payload).pipe(
          switchMap(location => {
            if (selectedLogo) {
              const formData = new FormData();
              formData.append('file', selectedLogo);
              return partnersService.updatePartnerLocationLogoV2(currentPartner.id, location.id, formData).pipe(
                switchMap(() => partnersService.getPartnerLocationByIdV2(currentPartner.id, location.id)),
                map(updatedLocation => PartnerActions.createLocationSuccess({ location: updatedLocation })),
                catchError(() => of(PartnerActions.createLocationSuccess({ location })))
              );
            }
            return of(PartnerActions.createLocationSuccess({ location }));
          }),
          catchError(error => of(PartnerActions.createLocationFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const editLocationEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.editLocation),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.editLocationFailure({ error: 'No current partner selected' }));
        }
        return partnersService.updatePartnerLocationV2(payload.partnerId, payload).pipe(
          map(location => PartnerActions.editLocationSuccess({ location })),
          catchError(error => of(PartnerActions.editLocationFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const activateLocationEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.activateLocation),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.activateLocationFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerLocationByIdV2(currentPartner.id, id).pipe(
          switchMap(location =>
            partnersService.updatePartnerLocationV2(currentPartner.id, { ...location, partnerId: currentPartner.id, active: true }).pipe(
              map(updatedLocation => PartnerActions.activateLocationSuccess({ location: updatedLocation })),
              catchError(error => of(PartnerActions.activateLocationFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.activateLocationFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deactivateLocationEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deactivateLocation),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deactivateLocationFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerLocationByIdV2(currentPartner.id, id).pipe(
          switchMap(location =>
            partnersService.updatePartnerLocationV2(currentPartner.id, { ...location, partnerId: currentPartner.id, active: false }).pipe(
              map(updatedLocation => PartnerActions.deactivateLocationSuccess({ location: updatedLocation })),
              catchError(error => of(PartnerActions.deactivateLocationFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.deactivateLocationFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setLocationAvailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setLocationAvailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setLocationAvailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerLocationByIdV2(currentPartner.id, id).pipe(
          switchMap(location =>
            partnersService.updatePartnerLocationV2(currentPartner.id, { ...location, partnerId: currentPartner.id, available: true }).pipe(
              map(updatedLocation => PartnerActions.setLocationAvailableSuccess({ location: updatedLocation })),
              catchError(error => of(PartnerActions.setLocationAvailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setLocationAvailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setLocationUnavailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setLocationUnavailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setLocationUnavailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerLocationByIdV2(currentPartner.id, id).pipe(
          switchMap(location =>
            partnersService.updatePartnerLocationV2(currentPartner.id, { ...location, partnerId: currentPartner.id, available: false }).pipe(
              map(updatedLocation => PartnerActions.setLocationUnavailableSuccess({ location: updatedLocation })),
              catchError(error => of(PartnerActions.setLocationUnavailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setLocationUnavailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deleteLocationEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deleteLocation),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deleteLocationFailure({ error: 'No current partner selected' }));
        }
        return partnersService.deletePartnerLocationV2(currentPartner.id, id).pipe(
          map(() => PartnerActions.deleteLocationSuccess({ id })),
          catchError(error => of(PartnerActions.deleteLocationFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const uploadLocationLogoEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.uploadLocationLogo),
      switchMap(({ partnerId, locationId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return partnersService.updatePartnerLocationLogoV2(partnerId, locationId, formData).pipe(
          switchMap(() => partnersService.getPartnerLocationByIdV2(partnerId, locationId)),
          map(location => PartnerActions.uploadLocationLogoSuccess({ location })),
          catchError(error => of(PartnerActions.uploadLocationLogoFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

// ===== DOCTOR EFFECTS =====

export const setDoctorsFilterEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setDoctorsFilter),
      withLatestFrom(store.select(selectCurrentPartner)),
      map(([, currentPartner]) => {
        if (!currentPartner?.id) {
          return PartnerActions.getDoctorsListFailure({ error: 'No current partner selected' });
        }
        return PartnerActions.fetchDoctorsFirstPage({ partnerId: currentPartner.id });
      })
    );
  },
  { functional: true }
);

export const fetchDoctorsFirstPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchDoctorsFirstPage),
      tap(() => store.dispatch(PartnerActions.clearDoctorsList())),
      withLatestFrom(store.select(selectDoctorsFilter), store.select(selectDoctorsPageSize)),
      map(([{ partnerId }, filter, pageSize]) => {
        const payload = createDoctorsSearchPayload({ partnerId, pageSize, filter, continuationToken: '' });
        return PartnerActions.getDoctorsList({ payload });
      })
    );
  },
  { functional: true }
);

export const fetchDoctorsNextPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchDoctorsNextPage),
      withLatestFrom(store.select(selectDoctorsFilter), store.select(selectDoctorsPageSize), store.select(selectDoctorsContinuationToken)),
      map(([{ partnerId }, filter, pageSize, continuationToken]) => {
        const payload = createDoctorsSearchPayload({ partnerId, pageSize, filter, continuationToken });
        return PartnerActions.getDoctorsList({ payload });
      })
    );
  },
  { functional: true }
);

export const getDoctorsListEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.getDoctorsList),
      switchMap(({ payload }) => {
        return partnersService.searchPartnerDoctorsV2(payload.partnerId, payload).pipe(
          map(response => PartnerActions.getDoctorsListSuccess({ response })),
          catchError(error => of(PartnerActions.getDoctorsListFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const getDoctorByIdEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.getDoctorById),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.getDoctorByIdFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerDoctorByIdV2(currentPartner.id, id).pipe(
          map(doctor => PartnerActions.getDoctorByIdSuccess({ doctor })),
          catchError(error => of(PartnerActions.getDoctorByIdFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const createDoctorEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.createDoctor),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.createDoctorFailure({ error: 'No current partner selected' }));
        }
        return partnersService.createPartnerDoctorV2(payload.partnerId, payload).pipe(
          map(doctor => PartnerActions.createDoctorSuccess({ doctor })),
          catchError(error => of(PartnerActions.createDoctorFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const editDoctorEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.editDoctor),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.editDoctorFailure({ error: 'No current partner selected' }));
        }
        return partnersService.updatePartnerDoctorV2(payload.partnerId, payload).pipe(
          map(doctor => PartnerActions.editDoctorSuccess({ doctor })),
          catchError(error => of(PartnerActions.editDoctorFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deleteDoctorEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deleteDoctor),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deleteDoctorFailure({ error: 'No current partner selected' }));
        }
        return partnersService.deletePartnerDoctorV2(currentPartner.id, id).pipe(
          map(() => PartnerActions.deleteDoctorSuccess({ id })),
          catchError(error => of(PartnerActions.deleteDoctorFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const activateDoctorEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.activateDoctor),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.activateDoctorFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerDoctorByIdV2(currentPartner.id, id).pipe(
          switchMap(doctor =>
            partnersService.updatePartnerDoctorV2(currentPartner.id, { ...doctor, partnerId: currentPartner.id, active: true }).pipe(
              map(updatedDoctor => PartnerActions.activateDoctorSuccess({ doctor: updatedDoctor })),
              catchError(error => of(PartnerActions.activateDoctorFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.activateDoctorFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deactivateDoctorEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deactivateDoctor),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deactivateDoctorFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerDoctorByIdV2(currentPartner.id, id).pipe(
          switchMap(doctor =>
            partnersService.updatePartnerDoctorV2(currentPartner.id, { ...doctor, partnerId: currentPartner.id, active: false }).pipe(
              map(updatedDoctor => PartnerActions.deactivateDoctorSuccess({ doctor: updatedDoctor })),
              catchError(error => of(PartnerActions.deactivateDoctorFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.deactivateDoctorFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setDoctorAvailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setDoctorAvailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setDoctorAvailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerDoctorByIdV2(currentPartner.id, id).pipe(
          switchMap(doctor =>
            partnersService.updatePartnerDoctorV2(currentPartner.id, { ...doctor, partnerId: currentPartner.id, available: true }).pipe(
              map(updatedDoctor => PartnerActions.setDoctorAvailableSuccess({ doctor: updatedDoctor })),
              catchError(error => of(PartnerActions.setDoctorAvailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setDoctorAvailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setDoctorUnavailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setDoctorUnavailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setDoctorUnavailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerDoctorByIdV2(currentPartner.id, id).pipe(
          switchMap(doctor =>
            partnersService.updatePartnerDoctorV2(currentPartner.id, { ...doctor, partnerId: currentPartner.id, available: false }).pipe(
              map(updatedDoctor => PartnerActions.setDoctorUnavailableSuccess({ doctor: updatedDoctor })),
              catchError(error => of(PartnerActions.setDoctorUnavailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setDoctorUnavailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

// ===== OFFERING EFFECTS =====

export const setOfferingsFilterEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setOfferingsFilter),
      withLatestFrom(store.select(selectCurrentPartner)),
      map(([, currentPartner]) => {
        if (!currentPartner?.id) {
          return PartnerActions.getOfferingsListFailure({ error: 'No current partner selected' });
        }
        return PartnerActions.fetchOfferingsFirstPage({ partnerId: currentPartner.id });
      })
    );
  },
  { functional: true }
);

export const fetchOfferingsFirstPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchOfferingsFirstPage),
      tap(() => store.dispatch(PartnerActions.clearOfferingsList())),
      withLatestFrom(store.select(selectOfferingsFilter), store.select(selectOfferingsPageSize)),
      map(([{ partnerId }, filter, pageSize]) => {
        const payload = createOfferingsSearchPayload({ partnerId, pageSize, filter, continuationToken: '' });
        return PartnerActions.getOfferingsList({ payload });
      })
    );
  },
  { functional: true }
);

export const fetchOfferingsNextPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.fetchOfferingsNextPage),
      withLatestFrom(store.select(selectOfferingsFilter), store.select(selectOfferingsPageSize), store.select(selectOfferingsContinuationToken)),
      map(([{ partnerId }, filter, pageSize, continuationToken]) => {
        const payload = createOfferingsSearchPayload({ partnerId, pageSize, filter, continuationToken });
        return PartnerActions.getOfferingsList({ payload });
      })
    );
  },
  { functional: true }
);

export const getOfferingsListEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService)) => {
    return actions$.pipe(
      ofType(PartnerActions.getOfferingsList),
      switchMap(({ payload }) => {
        return partnersService.searchPartnerOfferingsV2(payload.partnerId, payload).pipe(
          map(response => PartnerActions.getOfferingsListSuccess({ response })),
          catchError(error => of(PartnerActions.getOfferingsListFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const getOfferingByIdEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.getOfferingById),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.getOfferingByIdFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerOfferingByIdV2(currentPartner.id, id).pipe(
          map(offering => PartnerActions.getOfferingByIdSuccess({ offering })),
          catchError(error => of(PartnerActions.getOfferingByIdFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const createOfferingEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.createOffering),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.createOfferingFailure({ error: 'No current partner selected' }));
        }
        return partnersService.createPartnerOfferingV2(payload.partnerId, payload).pipe(
          map(offering => PartnerActions.createOfferingSuccess({ offering })),
          catchError(error => of(PartnerActions.createOfferingFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const editOfferingEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.editOffering),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ payload }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.editOfferingFailure({ error: 'No current partner selected' }));
        }
        return partnersService.updatePartnerOfferingV2(payload.partnerId, payload).pipe(
          map(offering => PartnerActions.editOfferingSuccess({ offering })),
          catchError(error => of(PartnerActions.editOfferingFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deleteOfferingEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deleteOffering),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deleteOfferingFailure({ error: 'No current partner selected' }));
        }
        return partnersService.deletePartnerOfferingV2(currentPartner.id, id).pipe(
          map(() => PartnerActions.deleteOfferingSuccess({ id })),
          catchError(error => of(PartnerActions.deleteOfferingFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const activateOfferingEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.activateOffering),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.activateOfferingFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerOfferingByIdV2(currentPartner.id, id).pipe(
          switchMap(offering =>
            partnersService.updatePartnerOfferingV2(currentPartner.id, { ...offering, partnerId: currentPartner.id, active: true }).pipe(
              map(updatedOffering => PartnerActions.activateOfferingSuccess({ offering: updatedOffering })),
              catchError(error => of(PartnerActions.activateOfferingFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.activateOfferingFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const deactivateOfferingEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.deactivateOffering),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.deactivateOfferingFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerOfferingByIdV2(currentPartner.id, id).pipe(
          switchMap(offering =>
            partnersService.updatePartnerOfferingV2(currentPartner.id, { ...offering, partnerId: currentPartner.id, active: false }).pipe(
              map(updatedOffering => PartnerActions.deactivateOfferingSuccess({ offering: updatedOffering })),
              catchError(error => of(PartnerActions.deactivateOfferingFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.deactivateOfferingFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setOfferingAvailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setOfferingAvailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setOfferingAvailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerOfferingByIdV2(currentPartner.id, id).pipe(
          switchMap(offering =>
            partnersService.updatePartnerOfferingV2(currentPartner.id, { ...offering, partnerId: currentPartner.id, available: true }).pipe(
              map(updatedOffering => PartnerActions.setOfferingAvailableSuccess({ offering: updatedOffering })),
              catchError(error => of(PartnerActions.setOfferingAvailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setOfferingAvailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);

export const setOfferingUnavailableEffect = createEffect(
  (actions$ = inject(Actions), partnersService = inject(PartnersService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PartnerActions.setOfferingUnavailable),
      withLatestFrom(store.select(selectCurrentPartner)),
      switchMap(([{ id }, currentPartner]) => {
        if (!currentPartner?.id) {
          return of(PartnerActions.setOfferingUnavailableFailure({ error: 'No current partner selected' }));
        }
        return partnersService.getPartnerOfferingByIdV2(currentPartner.id, id).pipe(
          switchMap(offering =>
            partnersService.updatePartnerOfferingV2(currentPartner.id, { ...offering, partnerId: currentPartner.id, available: false }).pipe(
              map(updatedOffering => PartnerActions.setOfferingUnavailableSuccess({ offering: updatedOffering })),
              catchError(error => of(PartnerActions.setOfferingUnavailableFailure({ error })))
            )
          ),
          catchError(error => of(PartnerActions.setOfferingUnavailableFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);
